import React, { useMemo, useState } from "react";
import { Receipt, Info, ShieldCheck, Send, Download, Sparkles, Landmark } from "lucide-react";

const SEED = [
  { id: "f1", number: "FAC-2026-0012", date: "2026-06-28", client: "M. Bernard", type: "nomenclature", ht: 25, rate: 0, sesam: "standard", sent: true },
  { id: "f2", number: "FAC-2026-0013", date: "2026-07-01", client: "Mme Diallo", type: "hors", ht: 120, rate: 20, sesam: "", sent: true },
  { id: "f3", number: "FAC-2026-0014", date: "2026-07-02", client: "M. Lopez", type: "nomenclature", ht: 25, rate: 0, sesam: "sans_vitale", sent: false },
];

export default function SandboxFacturation({ brand }) {
  const [invoices, setInvoices] = useState(SEED);
  const [type, setType] = useState("nomenclature");
  const [ht, setHt] = useState(60);
  const [auto, setAuto] = useState(true);
  const exon = type === "nomenclature";
  const tva = exon ? 0 : Math.round(ht * 20) / 100;

  const totals = useMemo(() => ({
    exo: invoices.filter((i) => i.type === "nomenclature").reduce((s, i) => s + i.ht, 0),
    tvaHt: invoices.filter((i) => i.type === "hors").reduce((s, i) => s + i.ht, 0),
    tvaCol: invoices.filter((i) => i.type === "hors").reduce((s, i) => s + i.ht * i.rate / 100, 0),
  }), [invoices]);

  const emit = () => {
    const n = invoices.length + 12 + 3;
    setInvoices((p) => [{
      id: `x${Date.now()}`, number: `FAC-2026-${String(n).padStart(4, "0")}`,
      date: new Date().toISOString().slice(0, 10), client: "Patient démo",
      type, ht, rate: exon ? 0 : 20, sesam: "standard", sent: auto,
    }, ...p]);
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="sandbox-facturation">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--bp)" }}>
        <Receipt size={13} /> Facturation électronique & comptable
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9.5px] normal-case tracking-normal">
          <Sparkles size={9} /> Démo interactive
        </span>
      </div>
      <h2 className="mt-2 font-display text-slate-900 text-2xl font-semibold">
        Factur-X, TVA santé et transmission comptable automatique
      </h2>

      <div className="mt-3 grid md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3.5 flex items-start gap-2.5 text-[12px] text-slate-700 leading-relaxed">
          <Info size={14} className="text-sky-600 mt-0.5 shrink-0" />
          <span><b>Échéance 1er sept. 2026</b> : vos actes de nomenclature restent exonérés de TVA
          (art. 261-4 CGI) — seuls les actes <b>hors nomenclature</b> exigent Factur-X + e-reporting.
          {brand?.name || "Votre plateforme"} fait le tri automatiquement.</span>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 flex items-start gap-2.5 text-[12px] text-slate-700 leading-relaxed">
          <ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <span><b>Conforme ISCA</b> : XML CII EN 16931 embarqué dans chaque PDF, archivage à valeur
          probante, journal de transmissions horodaté.</span>
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-[1fr_300px] gap-4 items-start">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[13px] font-semibold text-slate-900 mb-3">Émettre une facture</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <button onClick={() => setType("nomenclature")} data-testid="sbx-fact-nomenclature"
                    className={`text-left rounded-lg border p-3 ${exon ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-sky-300"}`}>
              <div className="text-[12px] font-semibold text-slate-900">Acte nomenclature</div>
              <div className="text-[10.5px] text-slate-500">Exonéré TVA — art. 261-4 CGI</div>
            </button>
            <button onClick={() => setType("hors")} data-testid="sbx-fact-hors"
                    className={`text-left rounded-lg border p-3 ${!exon ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-sky-300"}`}>
              <div className="text-[12px] font-semibold text-slate-900">Hors nomenclature</div>
              <div className="text-[10.5px] text-slate-500">TVA 20 % — Factur-X requis</div>
            </button>
          </div>
          <div className="mt-3 flex items-end gap-3 flex-wrap">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Montant HT (€)</span>
              <input type="number" value={ht} onChange={(e) => setHt(parseFloat(e.target.value) || 0)}
                     data-testid="sbx-fact-ht"
                     className="w-32 h-9 rounded-lg border border-slate-200 px-3 text-[13px] text-slate-900 outline-none focus:border-sky-400" />
            </label>
            <div className="text-[12.5px] text-slate-600 pb-2" data-testid="sbx-fact-total">
              {exon ? <>TTC <b className="text-slate-900">{ht.toFixed(2)} €</b> (TVA non applicable)</>
                    : <>+ TVA {tva.toFixed(2)} € → TTC <b className="text-slate-900">{(ht + tva).toFixed(2)} €</b></>}
            </div>
            <button onClick={emit} data-testid="sbx-fact-emit"
                    className="h-9 px-5 rounded-full text-white text-[12.5px] font-medium" style={{ background: "var(--bp)" }}>
              Émettre (Factur-X)
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-900">
            <Landmark size={14} style={{ color: "var(--bp)" }} /> Mandat comptable
          </div>
          <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">cabinet@expert-comptable-demo.fr</p>
          <label className="mt-3 inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)}
                   data-testid="sbx-fact-auto" className="h-4 w-4" style={{ accentColor: "var(--bp)" }} />
            <span className="text-[12px] text-slate-800">Transmission automatique</span>
          </label>
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600">
            {invoices.filter((i) => i.sent).length} facture(s) transmise(s) ce mois — journal horodaté conservé 6 ans.
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] font-semibold text-slate-900">Factures 2026</div>
          <div className="flex gap-4 text-[11px] text-slate-500">
            <span>Exonéré : <b className="text-slate-800">{totals.exo.toFixed(0)} €</b></span>
            <span>Hors nomencl. HT : <b className="text-slate-800">{totals.tvaHt.toFixed(0)} €</b></span>
            <span>TVA collectée : <b className="text-slate-800">{totals.tvaCol.toFixed(2)} €</b></span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                <th className="pb-2 pr-3">N°</th><th className="pb-2 pr-3">Client</th>
                <th className="pb-2 pr-3">Régime</th><th className="pb-2 pr-3">TTC</th>
                <th className="pb-2 pr-3">Comptable</th><th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => {
                const e = i.type === "nomenclature";
                const ttc = i.ht * (1 + i.rate / 100);
                return (
                  <tr key={i.id} className="border-t border-slate-100" data-testid={`sbx-inv-${i.id}`}>
                    <td className="py-2 pr-3 text-[12px] font-medium text-slate-900 whitespace-nowrap">{i.number}</td>
                    <td className="py-2 pr-3 text-[12px] text-slate-700">{i.client}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[9.5px] font-semibold ${
                        e ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"}`}>
                        {e ? "Exonéré 261-4" : `TVA ${i.rate} %`}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-[12px] font-semibold text-slate-900">{ttc.toFixed(2)} €</td>
                    <td className="py-2 pr-3 text-[10.5px]">{i.sent ? <span className="text-emerald-600 font-medium">Transmise</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-1.5 justify-end text-slate-500">
                        <Download size={13} className="hover:text-slate-900 cursor-pointer" />
                        <Send size={13} className="cursor-pointer" style={{ color: "var(--bp)" }}
                              onClick={() => setInvoices((p) => p.map((x) => x.id === i.id ? { ...x, sent: true } : x))} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[10.5px] text-slate-400">
          Version démo — sur votre plateforme réelle : PDF Factur-X téléchargeables (XML CII EN 16931 embarqué),
          export e-reporting CSV et envoi automatique au cabinet comptable.
        </p>
      </div>
    </div>
  );
}
